import { ref } from 'vue'
import { db, storage } from '@/firebase/firebase'
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  orderBy
} from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { compressAndConvertToWebP, generateWebPFileName } from '@/utils/imageUtils'

export function useProperty() {
  const properties = ref([])
  const currentProperty = ref(null)
  const loading = ref(false)
  const error = ref(null)

  /**
   * 이미지를 압축하고 Firebase Storage에 업로드
   * @param {File} file - 원본 이미지 파일
   * @param {string} path - 저장 경로 (예: 'properties/abc123')
   * @returns {Promise<string>} - 다운로드 URL
   */
  const uploadImage = async (file, path) => {
    const webpBlob = await compressAndConvertToWebP(file)
    const fileName = generateWebPFileName(file.name)
    const fileRef = storageRef(storage, `${path}/${fileName}`)

    await uploadBytes(fileRef, webpBlob, { contentType: 'image/webp' })
    return await getDownloadURL(fileRef)
  }

  /**
   * 현장 목록 조회
   */
  const fetchProperties = async () => {
    loading.value = true
    error.value = null

    try {
      const q = query(collection(db, 'properties'), orderBy('createdAt', 'desc'))
      const snapshot = await getDocs(q)

      properties.value = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (e) {
      error.value = '현장 목록을 불러오는데 실패했습니다.'
      console.error(e)
    } finally {
      loading.value = false
    }
  }

  /**
   * 단일 현장 조회
   * @param {string} id - 현장 ID
   */
  const fetchProperty = async (id) => {
    loading.value = true
    error.value = null

    try {
      const docSnap = await getDoc(doc(db, 'properties', id))

      if (docSnap.exists()) {
        currentProperty.value = {
          id: docSnap.id,
          ...docSnap.data()
        }
      } else {
        error.value = '현장을 찾을 수 없습니다.'
        currentProperty.value = null
      }
    } catch (e) {
      error.value = '현장 정보를 불러오는데 실패했습니다.'
      console.error(e)
    } finally {
      loading.value = false
    }
  }

  /**
   * 현장 등록
   * @param {Object} propertyData - 현장 데이터
   * @param {File} thumbnailFile - 대표 이미지 파일
   * @param {Array} typeImages - 타입별 이미지 파일 배열 [{typeIndex, file}]
   */
  const createProperty = async (propertyData, thumbnailFile = null, typeImages = []) => {
    loading.value = true
    error.value = null

    try {
      // 현장 문서 먼저 생성 (ID 확보)
      const docRef = await addDoc(collection(db, 'properties'), {
        ...propertyData,
        thumbnailUrl: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      const propertyId = docRef.id
      let thumbnailUrl = ''

      // 대표 이미지 업로드
      if (thumbnailFile) {
        thumbnailUrl = await uploadImage(thumbnailFile, `properties/${propertyId}`)
      }

      // 타입별 이미지 업로드
      const types = [...propertyData.types]
      for (const { typeIndex, file } of typeImages) {
        if (file && types[typeIndex]) {
          const imageUrl = await uploadImage(file, `properties/${propertyId}/types`)
          types[typeIndex].imageUrl = imageUrl
        }
      }

      // 이미지 URL 업데이트
      await updateDoc(docRef, {
        thumbnailUrl,
        types
      })

      return { success: true, id: propertyId }
    } catch (e) {
      error.value = '현장 등록에 실패했습니다.'
      console.error(e)
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  /**
   * 현장 수정
   * @param {string} id - 현장 ID
   * @param {Object} propertyData - 수정할 데이터
   * @param {File} thumbnailFile - 새 대표 이미지 (선택)
   * @param {Array} typeImages - 새 타입 이미지 배열 (선택)
   */
  const updateProperty = async (id, propertyData, thumbnailFile = null, typeImages = []) => {
    loading.value = true
    error.value = null

    try {
      const updateData = {
        ...propertyData,
        updatedAt: new Date().toISOString()
      }

      // 새 대표 이미지 업로드
      if (thumbnailFile) {
        updateData.thumbnailUrl = await uploadImage(thumbnailFile, `properties/${id}`)
      }

      // 타입별 새 이미지 업로드
      if (typeImages.length > 0) {
        const types = [...propertyData.types]
        for (const { typeIndex, file } of typeImages) {
          if (file && types[typeIndex]) {
            const imageUrl = await uploadImage(file, `properties/${id}/types`)
            types[typeIndex].imageUrl = imageUrl
          }
        }
        updateData.types = types
      }

      await updateDoc(doc(db, 'properties', id), updateData)

      return { success: true }
    } catch (e) {
      error.value = '현장 수정에 실패했습니다.'
      console.error(e)
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  /**
   * 현장 삭제
   * @param {string} id - 현장 ID
   */
  const deleteProperty = async (id) => {
    loading.value = true
    error.value = null

    try {
      // TODO: Storage 이미지도 삭제 (나중에 구현)
      await deleteDoc(doc(db, 'properties', id))
      properties.value = properties.value.filter((p) => p.id !== id)

      return { success: true }
    } catch (e) {
      error.value = '현장 삭제에 실패했습니다.'
      console.error(e)
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  return {
    // 상태
    properties,
    currentProperty,
    loading,
    error,

    // 메서드
    fetchProperties,
    fetchProperty,
    createProperty,
    updateProperty,
    deleteProperty,
    uploadImage
  }
}
